import { AxiosError } from "axios"

import { ErrorCode } from "@shared/enums/errorCode"

/** Side effect to run when a rejected response carries the registered error code. */
export type ErrorCodeHandler = (error: AxiosError) => void

/**
 * Feature-registered handlers for expected error codes on rejected responses.
 *
 * `onResponseRejected` stays generic: when a response's `errorCode` has a
 * registered handler it runs that handler instead of the toast flow. Features
 * register what "handling" means for their codes (e.g. `api/index.ts` refreshes
 * the access-derived queries on `PracticeConfigurationAccessDenied`).
 *
 * Registration is late-bound (instead of the interceptor importing handlers)
 * because handlers typically need `api`, which the interceptor cannot import
 * without a cycle (api → services → AxiosInterceptor). Same pattern as
 * `publicAxiosHooks`.
 */
const errorCodeHandlers = new Map<string, ErrorCodeHandler>()

/**
 * Register `handler` for `errorCode`, replacing any previously registered
 * handler for that code. Returns an unregister function.
 */
export const registerErrorCodeHandler = (errorCode: ErrorCode, handler: ErrorCodeHandler): (() => void) => {
  errorCodeHandlers.set(errorCode, handler)
  return () => errorCodeHandlers.delete(errorCode)
}

/** The handler registered for `errorCode`, if any. */
export const getErrorCodeHandler = (errorCode: unknown): ErrorCodeHandler | undefined =>
  typeof errorCode === "string" ? errorCodeHandlers.get(errorCode) : undefined
